import {
  Alert,
  Button,
  Card,
  Collapse,
  Form,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from "antd"
import { ColumnsType } from "antd/lib/table"
import type { RcFile } from "antd/lib/upload/interface"
import { UploadOutlined, CheckCircleOutlined, WarningOutlined, SyncOutlined } from "@ant-design/icons"
import { useMemo, useRef, useState } from "react"
import { PixelsEditorV2 } from "components"
import { useFileUpload } from "hooks"
import { Pixel, PixelEntry, SpecialEventPresetsConfigType } from "domains/managingJson/store/types"
import { Notification, NotificationsPerGame } from "domains/specialEvents/pages/main"
import {
  appendUniqueImageSource,
  getGlobalImageSources,
  normalizePixels,
  normalizeSourceImagesByGeo,
} from "domains/specialEvents/normalization"
import {
  appendMissingPixels,
  buildInternalPixelUrl,
  findTargetsForFileName,
  getPlacementSub3,
  getPlacementTargets,
  getGamePreset,
  getPresetForNotification,
  getTargetLabel,
  hasExternalPixel,
  hasInternalPixel,
  isNotificationUsingPreset,
  resetNotificationToPreset,
  SpecialEventPlacementTarget,
  upsertInternalPixel,
} from "domains/specialEvents/presets"
import Styles from "./styles.module.css"

const { Text } = Typography
const { Panel } = Collapse

type AutomationProps = {
  presetsConfig: SpecialEventPresetsConfigType;
  onOpenEvents?: () => void;
}

type NotificationRow = {
  key: string;
  gameIndex: number;
  notificationIndex: number;
  gameId: number;
  notification: Notification;
}

const getNotificationsPerGame = (form: ReturnType<typeof Form.useFormInstance>): NotificationsPerGame[] => {
  return form.getFieldValue("notificationsPerGame") || []
}

const setNotificationsPerGame = (
  form: ReturnType<typeof Form.useFormInstance>,
  notificationsPerGame: NotificationsPerGame[],
) => {
  form.setFieldsValue({ notificationsPerGame })
}

const updateNotification = (
  notificationsPerGame: NotificationsPerGame[],
  gameIndex: number,
  notificationIndex: number,
  updater: (notification: Notification) => Notification,
): NotificationsPerGame[] => {
  const gameConfig = notificationsPerGame[gameIndex]
  const notification = gameConfig?.notifications?.[notificationIndex]
  if (!gameConfig || !notification) return notificationsPerGame

  const notifications = [...(gameConfig.notifications || [])]
  notifications[notificationIndex] = updater(notification)

  const next = [...notificationsPerGame]
  next[gameIndex] = { ...gameConfig, notifications }

  return next
}

const updateTargetPixels = (
  notificationsPerGame: NotificationsPerGame[],
  target: SpecialEventPlacementTarget,
  updater: (pixels: Pixel[]) => Pixel[],
): NotificationsPerGame[] => {
  const gameConfig = notificationsPerGame[target.gameIndex]
  if (!gameConfig) return notificationsPerGame

  if (target.type === "notification") {
    if (target.notificationIndex === undefined) return notificationsPerGame

    return updateNotification(
      notificationsPerGame,
      target.gameIndex,
      target.notificationIndex,
      notification => ({
        ...notification,
        trackingPixels: updater(normalizePixels(notification.trackingPixels)),
      }),
    )
  }

  const bannerType = target.type
  const currentBanner = gameConfig[bannerType] || { image: "", cta: "", trackingPixels: [] }
  const next = [...notificationsPerGame]
  next[target.gameIndex] = {
    ...gameConfig,
    [bannerType]: {
      ...currentBanner,
      image: currentBanner.image || "",
      cta: currentBanner.cta || "",
      trackingPixels: updater(normalizePixels(currentBanner.trackingPixels)),
    },
  }

  return next
}

const updateTargetImage = (
  notificationsPerGame: NotificationsPerGame[],
  target: SpecialEventPlacementTarget,
  src: string,
): NotificationsPerGame[] => {
  const gameConfig = notificationsPerGame[target.gameIndex]
  if (!gameConfig) return notificationsPerGame

  if (target.type === "notification") {
    if (target.notificationIndex === undefined) return notificationsPerGame

    return updateNotification(
      notificationsPerGame,
      target.gameIndex,
      target.notificationIndex,
      notification => {
        const sourceImagesByGeo = normalizeSourceImagesByGeo(notification.sourceImagesByGeo)
        const globalIndex = sourceImagesByGeo.findIndex(source => source.country === "global")
        if (globalIndex >= 0) {
          sourceImagesByGeo[globalIndex] = {
            ...sourceImagesByGeo[globalIndex],
            imageSources: appendUniqueImageSource(sourceImagesByGeo[globalIndex].imageSources, src),
          }
        } else {
          sourceImagesByGeo.unshift({ country: "global", imageSources: [{ src }] })
        }

        return { ...notification, sourceImagesByGeo }
      },
    )
  }

  const bannerType = target.type
  const currentBanner = gameConfig[bannerType] || { image: "", cta: "", trackingPixels: [] }
  const next = [...notificationsPerGame]
  next[target.gameIndex] = {
    ...gameConfig,
    [bannerType]: {
      ...currentBanner,
      image: src,
      cta: currentBanner.cta || "",
      trackingPixels: normalizePixels(currentBanner.trackingPixels),
    },
  }

  return next
}

const getNotificationRows = (notificationsPerGame: NotificationsPerGame[]): NotificationRow[] => {
  return notificationsPerGame.flatMap((gameConfig, gameIndex) => (
    gameConfig.notifications?.map((notification, notificationIndex) => ({
      key: `${gameConfig.gameId}-${notification.uid || notificationIndex}`,
      gameIndex,
      notificationIndex,
      gameId: Number(gameConfig.gameId),
      notification,
    })) || []
  ))
}

const getGlobalImageCount = (notification: Notification) => getGlobalImageSources(notification).filter(source => source.src).length

const renderPixelTags = (pixels?: PixelEntry[] | null) => (
  <div className={Styles.statusTags}>
    <Tag color={hasInternalPixel(pixels) ? "success" : "error"}>Internal</Tag>
    <Tag color={hasExternalPixel(pixels) ? "success" : "default"}>External</Tag>
  </div>
)

const renderNumberOrNotSet = (value: number | undefined, suffix = "") => (
  value ? <Text>{value}{suffix}</Text> : <Tag className={Styles.notSetTag}>Not set</Tag>
)

const formatTargetList = (targets: SpecialEventPlacementTarget[]) => {
  const labels = targets.slice(0, 3).map(target => `${target.gameName} - ${target.label}`)
  const remaining = targets.length - labels.length

  return `${labels.join(", ")}${remaining > 0 ? `, +${remaining} more` : ""}`
}

export const SpecialEventsOverview = ({ presetsConfig, onOpenEvents }: AutomationProps) => {
  const form = Form.useFormInstance()
  const notificationsPerGame = Form.useWatch("notificationsPerGame", form) as NotificationsPerGame[] | undefined
  const rows = getNotificationRows(notificationsPerGame || [])
  const targets = getPlacementTargets(notificationsPerGame, presetsConfig)
  const missingAssets = targets.filter(target => target.type === "notification" && !target.currentImage)
  const missingInternalPixels = targets.filter(target => !hasInternalPixel(target.pixels))
  const customTriggerRows = rows.filter(row => (
    getGamePreset(presetsConfig, row.gameId)
    && !getPresetForNotification(presetsConfig, row.gameId, row.notification.triggerEventName)
  ))
  const unsupportedEnabledRows = rows.filter(row => (
    row.notification.enabled !== false
    && getPresetForNotification(presetsConfig, row.gameId, row.notification.triggerEventName)?.codeSupported === false
  ))

  const columns: ColumnsType<NotificationRow> = [
    {
      title: "Friendly Event",
      render: (_, row) => {
        const preset = getPresetForNotification(presetsConfig, row.gameId, row.notification.triggerEventName)

        return (
          <Space direction="vertical" size={0}>
            <Tooltip title={preset?.description}>
              <Text strong>{preset?.friendlyName || row.notification.triggerEventName || "Custom event"}</Text>
            </Tooltip>
            {preset?.recommendationScore && <Text className={Styles.mutedText}>{preset.recommendationScore}</Text>}
          </Space>
        )
      },
    },
    {
      title: "Trigger Key",
      dataIndex: ["notification", "triggerEventName"],
      width: 180,
      render: value => <Text code>{value || "missing"}</Text>,
    },
    {
      title: "Reward",
      width: 110,
      render: (_, row) => renderNumberOrNotSet(row.notification.buffsReward, " Buffs"),
    },
    {
      title: "Delay/Duration",
      width: 180,
      render: (_, row) => (
        <Space size={4}>
          {renderNumberOrNotSet(row.notification.triggerDelay, "s")}
          <Text className={Styles.mutedText}>/</Text>
          {renderNumberOrNotSet(row.notification.duration, "s")}
        </Space>
      ),
    },
    {
      title: "Asset",
      width: 110,
      render: (_, row) => {
        const count = getGlobalImageCount(row.notification)

        return <Tag color={count ? "success" : "error"}>{count ? `${count} ready` : "Missing"}</Tag>
      },
    },
    {
      title: "Pixels",
      width: 170,
      render: (_, row) => renderPixelTags(row.notification.trackingPixels),
    },
    {
      title: "Preset",
      width: 120,
      render: (_, row) => {
        const preset = getPresetForNotification(presetsConfig, row.gameId, row.notification.triggerEventName)
        const isDefault = isNotificationUsingPreset(row.notification, preset, presetsConfig)

        return <Tag color={isDefault ? "blue" : "gold"}>{isDefault ? "Default" : "Custom"}</Tag>
      },
    },
    {
      title: "Actions",
      width: 180,
      render: (_, row) => {
        const preset = getPresetForNotification(presetsConfig, row.gameId, row.notification.triggerEventName)

        return (
          <Space>
            <Button size="small" onClick={onOpenEvents}>Edit</Button>
            <Button
              size="small"
              disabled={!preset}
              onClick={() => {
                if (!preset) return
                const current = getNotificationsPerGame(form)
                const next = updateNotification(
                  current,
                  row.gameIndex,
                  row.notificationIndex,
                  notification => resetNotificationToPreset(notification, preset, presetsConfig),
                )
                setNotificationsPerGame(form, next)
              }}
            >
              Reset
            </Button>
          </Space>
        )
      },
    },
  ]

  const games = (notificationsPerGame || []).map((gameConfig, gameIndex) => ({
    gameIndex,
    gameId: Number(gameConfig.gameId),
    rows: rows.filter(row => row.gameIndex === gameIndex),
  }))

  return (
    <Space direction="vertical" size="middle" className={Styles.sectionStack}>
      {(missingAssets.length > 0
        || missingInternalPixels.length > 0
        || customTriggerRows.length > 0
        || unsupportedEnabledRows.length > 0) && (
        <Alert
          type="warning"
          showIcon
          className={Styles.compactWarning}
          message={(
            <Space direction="vertical" size={0}>
              {missingAssets.length > 0 && (
                <Text>{missingAssets.length} missing assets ({formatTargetList(missingAssets)})</Text>
              )}
              {missingInternalPixels.length > 0 && (
                <Text>{missingInternalPixels.length} missing internal pixels ({formatTargetList(missingInternalPixels)})</Text>
              )}
              {customTriggerRows.length > 0 && (
                <Text>
                  {customTriggerRows.length} custom trigger key{customTriggerRows.length === 1 ? "" : "s"}
                  not found in the maintained presets.
                  Verify that the client emits {customTriggerRows.length === 1 ? "it" : "them"} before saving.
                </Text>
              )}
              {unsupportedEnabledRows.length > 0 && (
                <Text>
                  {unsupportedEnabledRows.length} enabled trigger{unsupportedEnabledRows.length === 1 ? " is" : "s are"}
                  not shipped in the client. Disable or remove before saving.
                </Text>
              )}
            </Space>
          )}
        />
      )}
      {games.map(game => (
        <Card
          key={game.gameIndex}
          className={Styles.summaryCard}
          title={presetsConfig.games[String(game.gameId)]?.gameName || game.gameId}
          extra={<Text className={Styles.mutedText}>{game.rows.length} events</Text>}
        >
          <Table<NotificationRow>
            size="small"
            pagination={false}
            dataSource={game.rows}
            columns={columns}
            locale={{ emptyText: "No notifications yet. Open Game events to add presets or custom triggers." }}
          />
        </Card>
      ))}
    </Space>
  )
}

export const SpecialEventsPixelsPanel = ({ presetsConfig }: AutomationProps) => {
  const form = Form.useFormInstance()
  const notificationsPerGame = Form.useWatch("notificationsPerGame", form) as NotificationsPerGame[] | undefined
  const campaignName = Form.useWatch("name", form) || ""
  const targets = useMemo(() => getPlacementTargets(notificationsPerGame, presetsConfig), [notificationsPerGame, presetsConfig])
  const [partnerId, setPartnerId] = useState("")
  const [offerId, setOfferId] = useState("")
  const [baseUrl, setBaseUrl] = useState("https://www.bf3jdsk2d.com")
  const [externalPixels, setExternalPixels] = useState<Pixel[]>([{ src: "" }])

  const internalReady = targets.filter(target => hasInternalPixel(target.pixels)).length
  const externalReady = targets.filter(target => hasExternalPixel(target.pixels)).length

  const applyInternalPixels = () => {
    if (!partnerId || !offerId) return
    let next = getNotificationsPerGame(form)
    targets.forEach(target => {
      const generatedPixel = {
        src: buildInternalPixelUrl({
          partnerId,
          offerId,
          baseUrl,
          campaignName,
          placementSub3: getPlacementSub3(target),
        }),
      }
      next = updateTargetPixels(next, target, pixels => upsertInternalPixel(pixels, generatedPixel))
    })
    setNotificationsPerGame(form, next)
  }

  const applyExternalPixels = () => {
    const pixelsToApply = externalPixels.filter(pixel => pixel.src)
    if (pixelsToApply.length === 0) return
    let next = getNotificationsPerGame(form)
    targets.forEach(target => {
      next = updateTargetPixels(next, target, pixels => appendMissingPixels(pixels, pixelsToApply))
    })
    setNotificationsPerGame(form, next)
  }

  const setTargetPixels = (target: SpecialEventPlacementTarget, pixels: Pixel[]) => {
    const next = updateTargetPixels(getNotificationsPerGame(form), target, () => pixels)
    setNotificationsPerGame(form, next)
  }

  return (
    <Space direction="vertical" size="middle" className={Styles.sectionStack}>
      <Alert
        type="info"
        showIcon
        message={`Internal pixels: ${internalReady}/${targets.length}. External pixels: ${externalReady}/${targets.length}.`}
      />
      <Card title="Internal Everflow Pixel">
        <div className={Styles.toolbar}>
          <label className={Styles.toolbarField}>
            <Text>Partner ID</Text>
            <Input value={partnerId} onChange={e => setPartnerId(e.target.value)} placeholder="53LQ64W" />
          </label>
          <label className={Styles.toolbarField}>
            <Text>Offer ID</Text>
            <Input value={offerId} onChange={e => setOfferId(e.target.value)} placeholder="6Q3GCF" />
          </label>
          <label className={Styles.toolbarField}>
            <Text>Base URL</Text>
            <Input value={baseUrl} onChange={e => setBaseUrl(e.target.value)} />
          </label>
          <Button
            type="primary"
            icon={<SyncOutlined />}
            disabled={!partnerId || !offerId || targets.length === 0}
            onClick={applyInternalPixels}
          >
            Generate for all placements
          </Button>
        </div>
        <Text className={Styles.mutedText}>
          Internal pixels overwrite the existing BUFF/Everflow pixel per placement and keep non-BUFF client pixels.
        </Text>
      </Card>

      <Card title="External Client Pixels">
        <PixelsEditorV2 pixels={externalPixels} onChange={setExternalPixels} />
        <Button style={{ marginTop: 12 }} onClick={applyExternalPixels}>
          Apply external pixels to all placements
        </Button>
      </Card>

      <Card title="Placement Pixel Details">
        <Collapse>
          {targets.map(target => (
            <Panel
              key={target.id}
              header={(
                <Space>
                  <Text>{getTargetLabel(target)}</Text>
                  {renderPixelTags(target.pixels)}
                </Space>
              )}
            >
              <PixelsEditorV2 pixels={target.pixels || []} onChange={pixels => setTargetPixels(target, pixels)} />
            </Panel>
          ))}
        </Collapse>
      </Card>
    </Space>
  )
}

type UploadResult = {
  id: string;
  file: RcFile;
  fileName: string;
  url?: string;
  status: "ready" | "uploading" | "matched" | "needs-review" | "validation-error" | "failed";
  targetIds: string[];
  message?: string;
  validationError?: string;
}

const MAX_IMAGE_SIZE_MB = 2
const MAX_VIDEO_SIZE_MB = 50

const getAssetValidationError = (file: RcFile): string | undefined => {
  const lowerName = file.name.toLowerCase()
  const isImage = ["image/jpeg", "image/png", "image/svg+xml"].includes(file.type)
    || [".jpg", ".jpeg", ".png", ".svg"].some(extension => lowerName.endsWith(extension))
  const isVideo = file.type.startsWith("video/") || lowerName.endsWith(".webm")
  if (!isImage && !isVideo) return "Unsupported file type. Use JPG, PNG, SVG, or WebM/video."

  const maxSize = isVideo ? MAX_VIDEO_SIZE_MB : MAX_IMAGE_SIZE_MB
  if (file.size / 1024 / 1024 >= maxSize) return `File must be smaller than ${maxSize}MB.`

  return undefined
}

export const SpecialEventsAssetsPanel = ({ presetsConfig }: AutomationProps) => {
  const form = Form.useFormInstance()
  const { uploadFile, isLoading } = useFileUpload()
  const notificationsPerGame = Form.useWatch("notificationsPerGame", form) as NotificationsPerGame[] | undefined
  const targets = useMemo(() => getPlacementTargets(notificationsPerGame, presetsConfig), [notificationsPerGame, presetsConfig])
  const [results, setResults] = useState<UploadResult[]>([])
  const [isBatchUploading, setIsBatchUploading] = useState(false)
  const resultsRef = useRef<UploadResult[]>([])
  const uploadingIdsRef = useRef(new Set<string>())

  const updateResults = (updater: (current: UploadResult[]) => UploadResult[]) => {
    const next = updater(resultsRef.current)
    resultsRef.current = next
    setResults(next)
  }

  const resolveTargets = (targetIds: string[]) => {
    const liveTargets = getPlacementTargets(getNotificationsPerGame(form), presetsConfig)
    const resolvedTargets = targetIds.map(targetId => liveTargets.find(item => item.id === targetId))
    if (resolvedTargets.some(target => !target)) {
      throw new Error("A selected placement changed after staging. Review the assignment and try again.")
    }

    return resolvedTargets as SpecialEventPlacementTarget[]
  }

  const assignTargets = (targetIds: string[], src: string) => {
    if (!src || targetIds.length === 0) throw new Error("The upload did not return an asset URL.")
    let next = getNotificationsPerGame(form)
    const resolvedTargets = resolveTargets(targetIds)
    resolvedTargets.forEach(target => {
      next = updateTargetImage(next, target, src)
    })
    setNotificationsPerGame(form, next)
  }

  const stageFile = (file: RcFile) => {
    const lowerName = file.name.toLowerCase()
    if (lowerName === ".ds_store"
      || lowerName === "thumbs.db"
      || lowerName === "desktop.ini"
      || lowerName.startsWith("._")) return
    const fileName = file.webkitRelativePath || file.name
    const id = `${fileName}-${file.lastModified}-${file.size}`
    const validationError = getAssetValidationError(file)
    if (validationError) {
      updateResults(prev => [{
        id,
        file,
        fileName,
        status: "validation-error",
        targetIds: [],
        message: validationError,
        validationError,
      }, ...prev.filter(result => result.id !== id)])

      return
    }
    const matchedTargets = findTargetsForFileName(fileName, targets)
    const targetIds = matchedTargets.map(target => target.id)

    updateResults(prev => [{
      id,
      file,
      fileName,
      status: targetIds.length > 0 ? "ready" : "needs-review",
      targetIds,
      message: targetIds.length > 0
        ? `Ready for ${targetIds.length === 1 ? getTargetLabel(matchedTargets[0]) : `${targetIds.length} global placements`}`
        : "Choose a campaign placement before uploading",
    }, ...prev.filter(result => result.id !== id)])
  }

  const uploadResult = async(resultId: string) => {
    const result = resultsRef.current.find(item => item.id === resultId)
    if (!result || result.targetIds.length === 0 || !["ready", "failed"].includes(result.status)) return
    if (result.validationError) return
    if (uploadingIdsRef.current.has(resultId)) return
    uploadingIdsRef.current.add(resultId)
    updateResults(prev => prev.map(item => item.id === resultId
      ? { ...item, status: "uploading", message: "Uploading…" }
      : item))
    let uploadedUrl: string | undefined
    try {
      resolveTargets(result.targetIds)
      uploadedUrl = await uploadFile(result.file)
      if (typeof uploadedUrl !== "string" || !uploadedUrl.trim()) throw new Error("The upload did not return an asset URL.")
      assignTargets(result.targetIds, uploadedUrl)
      updateResults(prev => prev.map(item => item.id === resultId
        ? {
          ...item,
          url: uploadedUrl,
          status: "matched",
          message: `Assigned to ${result.targetIds.length} placement${result.targetIds.length === 1 ? "" : "s"}`,
        }
        : item))
    } catch (error) {
      const message = error instanceof Error ? error.message : "Upload failed"
      updateResults(prev => prev.map(item => item.id === resultId
        ? { ...item, url: uploadedUrl || item.url, status: "failed", message }
        : item))
    } finally {
      uploadingIdsRef.current.delete(resultId)
    }
  }

  const uploadReadyFiles = async() => {
    const readyResultIds = resultsRef.current
      .filter(result => result.status === "ready" && result.targetIds.length > 0)
      .map(result => result.id)
    setIsBatchUploading(true)
    try {
      for (const resultId of readyResultIds) {
        await uploadResult(resultId)
      }
    } finally {
      setIsBatchUploading(false)
    }
  }

  const targetOptions = targets.map(target => ({
    label: getTargetLabel(target),
    value: target.id,
  }))

  const missingTargets = targets.filter(target => target.isConfigured && !target.currentImage)
  const readyCount = results.filter(result => result.status === "ready" && result.targetIds.length > 0).length

  return (
    <Space direction="vertical" size="middle" className={Styles.sectionStack}>
      {missingTargets.length > 0 && (
        <Alert
          type="warning"
          showIcon
          className={Styles.compactWarning}
          message={`${missingTargets.length} missing assets (${formatTargetList(missingTargets)})`}
        />
      )}
      <Card
        title="Batch upload, review and assign"
        extra={(
          <Space wrap>
            <Upload
              multiple
              accept=".jpg,.jpeg,.png,.svg,.webm,image/jpeg,image/png,image/svg+xml,video/*"
              showUploadList={false}
              beforeUpload={file => {
                stageFile(file)

                return false
              }}
            >
              <Button icon={<UploadOutlined />}>Choose files</Button>
            </Upload>
            <Upload
              directory
              multiple
              accept=".jpg,.jpeg,.png,.svg,.webm,image/jpeg,image/png,image/svg+xml,video/*"
              showUploadList={false}
              beforeUpload={file => {
                stageFile(file)

                return false
              }}
            >
              <Button icon={<UploadOutlined />}>Choose folder</Button>
            </Upload>
            <Button
              type="primary"
              disabled={readyCount === 0}
              loading={isBatchUploading || isLoading}
              onClick={uploadReadyFiles}
            >
              Upload {readyCount} ready
            </Button>
          </Space>
        )}
      >
        <Space direction="vertical" className={Styles.sectionStack}>
          <Text className={Styles.mutedText}>
            Files are matched locally first and are not uploaded until you confirm. Client names such as
            Heart of the Beast_Fortnite_1_Kill_v1_1.webm are supported; ambiguous files stay in review.
          </Text>
          {results.length === 0 && <Text>No files selected yet.</Text>}
          {results.map(result => (
            <div className={Styles.uploadResult} key={result.id}>
              <Space direction="vertical" size={0}>
                <Text strong>{result.fileName}</Text>
                <Text className={Styles.targetHint}>{result.message}</Text>
                {result.url && <Text className={Styles.targetHint}>{result.url}</Text>}
              </Space>
              <Tag
                icon={["ready", "matched"].includes(result.status) ? <CheckCircleOutlined /> : <WarningOutlined />}
                color={result.status === "matched"
                  ? "success"
                  : result.status === "ready"
                    ? "blue"
                    : result.status === "uploading"
                      ? "processing"
                      : ["failed", "validation-error"].includes(result.status)
                        ? "error"
                        : "warning"}
              >
                {result.status}
              </Tag>
              <Select
                mode="multiple"
                placeholder="Assign manually"
                options={targetOptions}
                value={result.targetIds}
                disabled={isBatchUploading || ["uploading", "validation-error"].includes(result.status)}
                onChange={targetIds => updateResults(prev => prev.map(item => item.id === result.id
                  ? {
                    ...item,
                    targetIds,
                    status: targetIds.length > 0 ? "ready" : "needs-review",
                    message: targetIds.length > 0 ? "Manual assignment ready" : "Choose a campaign placement before uploading",
                  }
                  : item))}
                filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
              />
              <Button
                disabled={isBatchUploading
                  || result.targetIds.length === 0
                  || ["uploading", "matched", "validation-error"].includes(result.status)}
                loading={result.status === "uploading"}
                onClick={() => uploadResult(result.id)}
              >
                Upload & assign
              </Button>
            </div>
          ))}
        </Space>
      </Card>
    </Space>
  )
}
